import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import {
  useSettingsProfile,
  useUpdateProfile,
} from '@/v2/hooks/settings/useSettingsProfile'
import { useChangePassword } from '@/v2/hooks/settings/useChangePassword'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import PassField from '@/v2/components/common/PassField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import {
  SettingsPage,
  PrimaryButton,
  SecondaryButton,
  HeaderActions,
  Fieldset,
  FieldsetTitle,
  Row,
  Form,
} from '@/v2/components/settings/shared'

const NOTIFY_OPTIONS = [
  'appointment_scheduled',
  'appointment_canceled',
  'appointment_rescheduled',
  'new_message_received',
  'payment_processed',
]

const formatNotifyOption = option =>
  option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

const MyProfile = () => {
  const { profile, isLoading: profileLoading } = useSettingsProfile()
  const { updateProfile, isPending: loading } = useUpdateProfile()
  const { changePassword, isPending: passwordLoading } = useChangePassword()
  const { onOpenSnackbar } = useSnackbarContext()

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    notify_on: [],
  })
  const [hasChanges, setHasChanges] = useState(false)

  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  })
  const [passwordErrors, setPasswordErrors] = useState({})

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.firstName || '',
        last_name: profile.lastName || '',
        email: profile.email || '',
        notify_on: profile.notifyOn || [],
      })
    }
  }, [profile])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleNotifyChange = (option, checked) => {
    const updatedNotifyOn = checked
      ? [...formData.notify_on, option]
      : formData.notify_on.filter(item => item !== option)

    setFormData(prev => ({ ...prev, notify_on: updatedNotifyOn }))
    setHasChanges(true)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    try {
      await updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        notify_on: formData.notify_on,
      })
      setHasChanges(false)
      onOpenSnackbar('Profile updated successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to update profile', 'error')
    }
  }

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
    setPasswordErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const handlePasswordSubmit = async () => {
    setPasswordErrors({})

    try {
      await changePassword(passwordData)
      setPasswordData({
        current_password: '',
        password: '',
        password_confirmation: '',
      })
      setShowPasswordModal(false)
      onOpenSnackbar('Password updated successfully', 'success')
    } catch (error) {
      if (error?.errors) {
        setPasswordErrors(error.errors)
      } else {
        onOpenSnackbar('Failed to update password', 'error')
      }
    }
  }

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false)
    setPasswordData({
      current_password: '',
      password: '',
      password_confirmation: '',
    })
    setPasswordErrors({})
  }

  const passwordHasValues =
    passwordData.current_password &&
    passwordData.password &&
    passwordData.password_confirmation

  return (
    <SettingsPage
      title="My Profile"
      loading={profileLoading}
      actions={
        <PrimaryButton onClick={handleSubmit} disabled={loading || !hasChanges}>
          {loading ? 'Saving...' : 'Save'}
        </PrimaryButton>
      }
    >
      <Form onSubmit={handleSubmit}>
        <Fieldset>
          <FieldsetTitle>Personal info</FieldsetTitle>
          <Row $columns="1fr 1fr">
            <TextField
              fullWidth
              label="First name"
              placeholder="Your first name"
              value={formData.first_name}
              onChange={val => handleInputChange('first_name', val)}
            />
            <TextField
              fullWidth
              label="Last name"
              placeholder="Your last name"
              value={formData.last_name}
              onChange={val => handleInputChange('last_name', val)}
            />
          </Row>

          <Row $columns="1fr 1fr">
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              disabled
            />
          </Row>

          <Row>
            <div>
              <SecondaryButton
                type="button"
                onClick={() => setShowPasswordModal(true)}
              >
                Change password
              </SecondaryButton>
            </div>
          </Row>
        </Fieldset>

        <Fieldset>
          <FieldsetTitle>Notify me on:</FieldsetTitle>
          <Row $columns="1fr 1fr">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {NOTIFY_OPTIONS.map(option => (
                <CheckboxField
                  key={option}
                  label={formatNotifyOption(option)}
                  checked={formData.notify_on.includes(option)}
                  onChange={e => handleNotifyChange(option, e.target.checked)}
                />
              ))}
            </Box>
          </Row>
        </Fieldset>
      </Form>

      <Modal
        isOpen={showPasswordModal}
        title="Change password"
        onClose={handleClosePasswordModal}
        onConfirm={handlePasswordSubmit}
        confirmLabel={passwordLoading ? 'Updating...' : 'Update password'}
        submitDisabled={passwordLoading || !passwordHasValues}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PassField
            fullWidth
            label="Current password"
            placeholder="Enter current password"
            value={passwordData.current_password}
            onChange={val => handlePasswordChange('current_password', val)}
            error={passwordErrors.current_password?.[0]}
          />
          <PassField
            fullWidth
            label="New password"
            placeholder="Enter new password"
            value={passwordData.password}
            onChange={val => handlePasswordChange('password', val)}
            error={passwordErrors.password?.[0]}
          />
          <PassField
            fullWidth
            label="Confirm new password"
            placeholder="Confirm new password"
            value={passwordData.password_confirmation}
            onChange={val => handlePasswordChange('password_confirmation', val)}
            error={passwordErrors.password_confirmation?.[0]}
          />
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default MyProfile
